import { supabase } from "@/integrations/supabase/client";
import { trackInventoryChange } from "./inventoryTracker";
import { pushNotificationService } from "@/services/notifications/pushNotificationService";

// Get inventory items for a store
export const getInventoryItems = async (storeId: string, type?: string) => {
  try {
    let query = supabase
      .from("inventory_items")
      .select("*")
      .eq("store_id", storeId)
      .order("name");
    
    if (type) {
      query = query.eq("inventory_type", type);
    }
    
    const { data, error } = await query;

    if (error) {
      console.error("Error fetching inventory items:", error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error("Error fetching inventory items:", error);
    return [];
  }
};

// Get specifically advanced inventory items
export const getAdvancedInventoryItems = async (storeId: string) => {
  return getInventoryItems(storeId, "advanced");
};

// Get specifically manual inventory items
export const getManualInventoryItems = async (storeId: string) => {
  return getInventoryItems(storeId, "manual");
};

// Get menu items with inventory relationships
export const getMenuItemsWithInventory = async (storeId: string, type?: string) => {
  try {
    // First get menu items for the store
    const { data: menuItems, error: menuError } = await supabase
      .from("menu_items")
      .select("*")
      .eq("store_id", storeId);

    if (menuError) {
      throw menuError;
    }

    // For each menu item, get its inventory relationships
    const menuItemsWithRelationships = await Promise.all(
      menuItems.map(async (menuItem) => {
        let query = supabase
          .from("menu_item_inventory")
          .select(`
            *,
            inventory_items (*)
          `)
          .eq("menu_item_id", menuItem.id);
          
        if (type) {
          query = query.eq("inventory_items.inventory_type", type);
        }
        
        const { data: relationships, error: relError } = await query;

        if (relError) {
          console.error(`Error fetching relationships for menu item ${menuItem.id}:`, relError);
          return { ...menuItem, inventoryRelationships: [] };
        }

        return { ...menuItem, inventoryRelationships: relationships || [] };
      })
    );

    return menuItemsWithRelationships;
  } catch (error) {
    console.error("Error fetching menu items with inventory:", error);
    return [];
  }
};

// Get inventory relationships for a specific menu item
export const getMenuInventoryRelationships = async (menuItemId: string) => {
  try {
    const { data, error } = await supabase
      .from("menu_item_inventory")
      .select(`
        *,
        inventory_items (*)
      `)
      .eq("menu_item_id", menuItemId);

    if (error) {
      console.error("Error fetching menu-inventory relationships:", error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error("Error fetching menu-inventory relationships:", error);
    return [];
  }
};

// Update menu-inventory relationships
export const updateMenuInventoryRelationships = async (
  menuItemId: string,
  relationships: { inventoryId: string; quantity: number }[]
) => {
  try {
    // Get existing relationships
    const { data: existingRelationships, error: fetchError } = await supabase
      .from("menu_item_inventory")
      .select("*")
      .eq("menu_item_id", menuItemId);

    if (fetchError) {
      console.error("Error fetching existing relationships:", fetchError);
      return false;
    }

    // Get inventory item types to filter by advanced type
    const inventoryIds = relationships.map(r => r.inventoryId);
    
    if (inventoryIds.length === 0) {
      // Nothing to update if no relationships
      return true;
    }
    
    const { data: inventoryItems, error: inventoryError } = await supabase
      .from("inventory_items")
      .select("id, inventory_type")
      .in("id", inventoryIds);

    if (inventoryError) {
      console.error("Error fetching inventory types:", inventoryError);
      return false;
    }
    
    // Filter to only update advanced inventory relationships
    const advancedItemIds = inventoryItems
      .filter(item => item.inventory_type === "advanced")
      .map(item => item.id);
    
    const advancedRelationships = relationships
      .filter(rel => advancedItemIds.includes(rel.inventoryId));
      
    // Delete existing relationships for advanced inventory
    const { error: deleteError } = await supabase
      .from("menu_item_inventory")
      .delete()
      .eq("menu_item_id", menuItemId)
      .in("inventory_item_id", advancedItemIds);

    if (deleteError) {
      console.error("Error deleting existing relationships:", deleteError);
      return false;
    }

    // Insert new relationships
    if (advancedRelationships.length > 0) {
      const newRelationships = advancedRelationships.map((rel) => ({
        menu_item_id: menuItemId,
        inventory_item_id: rel.inventoryId,
        quantity_required: rel.quantity,
      }));

      const { error: insertError } = await supabase
        .from("menu_item_inventory")
        .insert(newRelationships);

      if (insertError) {
        console.error("Error inserting new relationships:", insertError);
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error("Error updating menu-inventory relationships:", error);
    return false;
  }
};

// Create or update a manual inventory item for a menu item
export const createManualInventoryItem = async (
  storeId: string,
  menuItemId: string,
  menuItemName: string,
  quantity: number,
  availabilityFrom?: Date,
  availabilityTo?: Date,
  alwaysAvailable: boolean = true
) => {
  try {
    // Check if there's an existing manual inventory item for this menu item
    const { data: existingItems, error: fetchError } = await supabase
      .from("menu_item_inventory")
      .select(`
        *,
        inventory_items!inner (*)
      `)
      .eq("menu_item_id", menuItemId)
      .eq("inventory_items.inventory_type", "manual");

    if (fetchError) {
      console.error("Error checking for existing manual inventory:", fetchError);
      return { success: false, error: fetchError };
    }

    let inventoryItemId;
    
    // Convert Date objects to ISO strings for Supabase
    const availabilityFromStr = availabilityFrom ? availabilityFrom.toISOString() : null;
    const availabilityToStr = availabilityTo ? availabilityTo.toISOString() : null;
    
    // If we found an existing manual inventory item, update it
    if (existingItems && existingItems.length > 0) {
      const existingItem = existingItems[0];
      inventoryItemId = existingItem.inventory_items.id;
      
      // Update the inventory item
      const { error: updateError } = await supabase
        .from("inventory_items")
        .update({
          current_quantity: quantity,
          always_available: alwaysAvailable,
          availability_from: availabilityFromStr,
          availability_to: availabilityToStr,
          updated_at: new Date().toISOString()
        })
        .eq("id", inventoryItemId);

      if (updateError) {
        console.error("Error updating manual inventory item:", updateError);
        return { success: false, error: updateError };
      }
    } else {
      // Create a new inventory item and relationship
      // 1. Create the manual inventory item
      const { data: newItem, error: createError } = await supabase
        .from("inventory_items")
        .insert({
          store_id: storeId,
          name: `${menuItemName} (Manual Inventory)`,
          current_quantity: quantity,
          inventory_type: "manual",
          always_available: alwaysAvailable,
          availability_from: availabilityFromStr,
          availability_to: availabilityToStr
        })
        .select();

      if (createError || !newItem) {
        console.error("Error creating manual inventory item:", createError);
        return { success: false, error: createError };
      }

      inventoryItemId = newItem[0].id;

      // 2. Create the menu-inventory relationship
      const { error: relError } = await supabase
        .from("menu_item_inventory")
        .insert({
          menu_item_id: menuItemId,
          inventory_item_id: inventoryItemId,
          quantity_required: 1 // Fixed quantity for manual inventory
        });

      if (relError) {
        console.error("Error creating inventory relationship:", relError);
        return { success: false, error: relError };
      }
    }

    return { success: true, inventoryItemId };
  } catch (error) {
    console.error("Error with manual inventory creation:", error);
    return { success: false, error };
  }
};

// Update inventory quantity with tracking
export const updateInventoryQuantity = async (inventoryItemId: string, quantity: number) => {
  try {
    // First get the current item details
    const { data: currentItem, error: fetchError } = await supabase
      .from("inventory_items")
      .select("current_quantity, minimum_quantity, name, store_id, unit_of_measure")
      .eq("id", inventoryItemId)
      .single();

    if (fetchError) {
      console.error("Error fetching current inventory item:", fetchError);
      return { success: false, error: fetchError };
    }

    const previousQuantity = currentItem.current_quantity;
    
    const { data, error } = await supabase
      .from("inventory_items")
      .update({ 
        current_quantity: quantity,
        updated_at: new Date().toISOString()
      })
      .eq("id", inventoryItemId)
      .select();

    if (error) {
      console.error("Error updating inventory quantity:", error);
      return { success: false, error };
    }

    // Track the inventory change for notifications
    try {
      await trackInventoryChange({
        inventoryItemId,
        storeId: currentItem.store_id,
        previousQuantity,
        currentQuantity: quantity,
        minimumQuantity: currentItem.minimum_quantity || 0,
        itemName: currentItem.name,
        unitOfMeasure: currentItem.unit_of_measure || "units"
      });
    } catch (trackingError) {
      console.warn("Inventory tracking failed (non-critical):", trackingError);
      // Don't fail the update if tracking fails
    }

    return { success: true, data };
  } catch (error) {
    console.error("Error updating inventory quantity:", error);
    return { success: false, error };
  }
};

// Deduct inventory from menu item with tracking
export const deductInventory = async (menuItemId: string, orderQuantity: number) => {
  try {
    // Get the inventory relationships for this menu item
    const { data: relationships, error: relError } = await supabase
      .from("menu_item_inventory")
      .select(`
        *,
        inventory_items (*)
      `)
      .eq("menu_item_id", menuItemId);

    if (relError) {
      console.error("Error fetching inventory relationships:", relError);
      return { success: false, error: relError };
    }

    // If no relationships found, nothing to deduct
    if (!relationships || relationships.length === 0) {
      return { success: true, message: "No inventory relationships found" };
    }

    // For each relationship, deduct the required quantity multiplied by order quantity
    for (const rel of relationships) {
      if (!rel.inventory_items) continue;
      
      const previousQuantity = rel.inventory_items.current_quantity;
      const newQuantity = Math.max(
        0, 
        previousQuantity - (rel.quantity_required * orderQuantity)
      );
      
      // Update the inventory item quantity
      const { error: updateError } = await supabase
        .from("inventory_items")
        .update({ 
          current_quantity: newQuantity,
          updated_at: new Date().toISOString()
        })
        .eq("id", rel.inventory_item_id);

      if (updateError) {
        console.error(`Error updating inventory quantity for item ${rel.inventory_item_id}:`, updateError);
        continue; // Continue to process other items even if one fails
      }

      // Track the inventory change for notifications
      try {
        await trackInventoryChange({
          inventoryItemId: rel.inventory_item_id,
          storeId: rel.inventory_items.store_id,
          previousQuantity,
          currentQuantity: newQuantity,
          minimumQuantity: rel.inventory_items.minimum_quantity || 0,
          itemName: rel.inventory_items.name,
          unitOfMeasure: rel.inventory_items.unit_of_measure || "units"
        });
      } catch (trackingError) {
        console.warn("Inventory tracking failed (non-critical):", trackingError);
        // Don't fail the deduction if tracking fails
      }
    }

    return { success: true };
  } catch (error) {
    console.error("Error deducting inventory:", error);
    return { success: false, error };
  }
};

// Sync menu item availability based on inventory
export const syncAllMenuItemsAvailability = async (storeId: string) => {
  try {
    // Get all menu items with their inventory relationships
    const menuItems = await getMenuItemsWithInventory(storeId);
    let updatedItems = 0;
    let unavailableItems = 0;
    
    // For each menu item, check if it has sufficient inventory
    for (const menuItem of menuItems) {
      const shouldBeAvailable = await checkMenuItemInventoryAvailability(menuItem);
      
      // If availability should change, update the menu item
      if (menuItem.available !== shouldBeAvailable) {
        const { error } = await supabase
          .from("menu_items")
          .update({ available: shouldBeAvailable })
          .eq("id", menuItem.id);
          
        if (error) {
          console.error(`Error updating availability for menu item ${menuItem.id}:`, error);
        } else {
          updatedItems++;
          if (!shouldBeAvailable) {
            unavailableItems++;
          }
        }
      }
    }
    
    return {
      success: true,
      totalItems: menuItems.length,
      updatedItems,
      unavailableItems
    };
  } catch (error) {
    console.error("Error syncing menu item availability:", error);
    return {
      success: false,
      error,
      totalItems: 0,
      updatedItems: 0,
      unavailableItems: 0
    };
  }
};

// Helper function to check menu item inventory availability
const checkMenuItemInventoryAvailability = async (menuItem) => {
  try {
    // If menu item has no inventory relationships, it's always available
    if (!menuItem.inventoryRelationships || menuItem.inventoryRelationships.length === 0) {
      return true;
    }
    
    // Check each inventory relationship
    for (const rel of menuItem.inventoryRelationships) {
      // Skip if inventory_items is not defined
      if (!rel.inventory_items) continue;
      
      // For manual inventory type
      if (rel.inventory_items.inventory_type === "manual") {
        // Check if always available
        if (rel.inventory_items.always_available) {
          continue; // Skip quantity check if always available
        }
        
        // Check date availability (if not always available)
        const now = new Date();
        const fromDate = rel.inventory_items.availability_from ? new Date(rel.inventory_items.availability_from) : null;
        const toDate = rel.inventory_items.availability_to ? new Date(rel.inventory_items.availability_to) : null;
        
        // Not available if current date is outside the availability window
        if (fromDate && now < fromDate) return false;
        if (toDate && now > toDate) return false;
        
        // Check quantity
        if (rel.inventory_items.current_quantity <= 0) {
          return false;
        }
      }
      
      // For advanced inventory type, check quantity
      else if (rel.inventory_items.inventory_type === "advanced") {
        // If quantity required is more than available, item is unavailable
        if (rel.quantity_required > rel.inventory_items.current_quantity) {
          return false;
        }
      }
    }
    
    // If all checks pass, the item is available
    return true;
  } catch (error) {
    console.error("Error checking menu item availability:", error);
    return true; // Default to available in case of errors
  }
};

export const updateAdvancedInventory = async (
  itemId: string,
  updates: Partial<{
    current_quantity: number;
    minimum_quantity: number;
    name: string;
    description: string;
    category: string;
    unit_of_measure: string;
    storage_location: string;
    supplier_name: string;
    reorder_point: number;
  }>
) => {
  try {
    // Get current item data
    const { data: currentItem, error: fetchError } = await supabase
      .from('inventory_items')
      .select('*')
      .eq('id', itemId)
      .single();

    if (fetchError) {
      console.error('Error fetching current inventory item:', fetchError);
      throw fetchError;
    }

    // Update the item
    const { data: updatedItem, error: updateError } = await supabase
      .from('inventory_items')
      .update(updates)
      .eq('id', itemId)
      .select()
      .single();

    if (updateError) {
      console.error('Error updating inventory item:', updateError);
      throw updateError;
    }

    // Check if we need to send inventory alerts
    if (updates.current_quantity !== undefined && currentItem) {
      const newQuantity = updates.current_quantity;
      const minimumQuantity = updates.minimum_quantity || currentItem.minimum_quantity || 0;
      
      // Determine alert type
      let alertType: 'low_stock' | 'out_of_stock' | 'critical' | null = null;
      
      if (newQuantity <= 0) {
        alertType = 'out_of_stock';
      } else if (newQuantity <= minimumQuantity * 0.2) {
        alertType = 'critical';
      } else if (newQuantity <= minimumQuantity) {
        alertType = 'low_stock';
      }

      // Send push notification if alert is needed
      if (alertType && updatedItem) {
        try {
          await pushNotificationService.notifyInventoryAlert(
            updatedItem,
            alertType,
            newQuantity,
            minimumQuantity
          );
        } catch (error) {
          console.error('Error sending inventory alert notification:', error);
        }
      }
    }

    return updatedItem;
  } catch (error) {
    console.error('Error in updateAdvancedInventory:', error);
    throw error;
  }
};
